import { usePostApi } from '@/apiError/apiCreate';
import { ShowAllData, useGetApiListNoToast } from '@/apiError/apiShow';
import { useUpdateApi } from '@/apiError/apiUpdate';
import { AntButton } from '@/common/ant-components/AntButton';
import { NewNepaliDatePicker } from '@/common/customNepaliDatePicker';
import {
  getAdFromBSInYMD,
  getBsFromAD,
  getTodayDate,
} from '@/common/custom-date-selector-list/helpers';
import { handleInputBlur } from '@/components/forms/reuse/CapitalizeWords';
import useAppData from '@/hooks/useAppData';
import { getTodaysDateMYD } from '@/utils/date/TodaysDate';
import { getAdFromBS } from '@/utils/helpers/getTodayDate';
import { handleKeyDown } from '@/utils/validation/FormValidation';
import {
  compressImage,
  customRequest,
  imageOnChange,
  tagProcessedFile,
} from '@/pages/admin/master/company/form/common';
import { UploadOutlined } from '@ant-design/icons';
import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Image,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import {
  ADDRESS_PARTS,
  FAMILY_RELATIONS,
  IDENTITY_DOCUMENT_HINTS,
  IDENTITY_DOCUMENTS,
  MARITAL_STATUSES,
  MAX_DOCUMENT_IMAGES,
  PAYMENT_MODES,
  SALUTATIONS,
} from './kycOptions';

const bodyStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '0 12px',
};

const familyFieldName = (relation) => `family_${relation}`;

const DOCUMENT_ACCEPT = '.jpg,.jpeg,.png';
const DOCUMENT_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_DOCUMENT_SIZE_KB = 512;

const addressFieldNames = ['permanent', 'temporary'].flatMap((prefix) =>
  ADDRESS_PARTS.map((part) => `${prefix}${part}`),
);

const KYC_STEPS = [
  {
    title: 'Personal Details',
    fields: [
      'salutation',
      'customerName',
      'maritalStatus',
      'phoneNo',
      'mobileNo',
      'panNo',
      'email',
    ],
  },
  {
    title: 'Address',
    fields: [...addressFieldNames, 'sameAsPermanentAddress'],
  },
  {
    title: 'Identification',
    fields: [
      'identityDocumentType',
      'identityDocumentNumber',
      'issuedPlace',
      'issuedEnglishDate',
      'issuedNepaliDate',
    ],
  },
  {
    title: 'Occupation & Bank',
    fields: [
      'occupation',
      'designation',
      'bankName',
      'bankAccountNo',
      'paymentMode',
      'fundSource',
    ],
  },
  {
    title: 'Family Members',
    fields: FAMILY_RELATIONS.map(({ value }) => familyFieldName(value)),
  },
];

const LAST_STEP = KYC_STEPS.length - 1;

const documentFileList = (documents = []) =>
  documents
    .filter((doc) => doc?.image)
    .map((doc) => ({
      uid: `document-${doc.id}`,
      documentId: doc.id,
      name: decodeURIComponent(
        doc.image.split('/').pop()?.split('?')[0] || 'document',
      ),
      status: 'done',
      url: doc.image,
    }));

const DualDateField = ({ form, adName, bsName, label, defaultMode }) => {
  const prefixName = `${adName}Prefix`;
  const pickerName = `${adName}Picker`;
  const mode = Form.useWatch(prefixName, form) || defaultMode;
  return (
    <div className="tw-grid lg:tw-grid-cols-12 lg:tw-gap-2">
      <Form.Item
        className="username lg:tw-col-span-4"
        label="AD/BS"
        name={prefixName}
        initialValue={defaultMode}
      >
        <Select>
          <Select.Option value="ad">AD</Select.Option>
          <Select.Option value="bs">BS</Select.Option>
        </Select>
      </Form.Item>
      <div className="lg:tw-col-span-8">
        <Form.Item className="username" label={label}>
          {mode === 'ad' ? (
            <Form.Item name={pickerName} noStyle>
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                allowClear={false}
                placeholder="Select date (AD)"
                onChange={(_, date) => {
                  if (!date) return;
                  form.setFieldsValue({
                    [adName]: date,
                    [bsName]: getBsFromAD(date),
                  });
                }}
                disabledDate={(current) =>
                  current && current > dayjs(getTodaysDateMYD()).endOf('day')
                }
              />
            </Form.Item>
          ) : (
            <NewNepaliDatePicker
              allowClear={false}
              placeholder="Select date (BS)"
              formProps={{ name: bsName, noStyle: true }}
              disabledAfter={getTodayDate()}
              onChange={(value) => {
                if (!value) return;
                const adDate = getAdFromBS(value);
                form.setFieldsValue({
                  [adName]: getAdFromBSInYMD(adDate),
                  [pickerName]: adDate,
                });
              }}
            />
          )}
          <Form.Item name={adName} hidden>
            <Input />
          </Form.Item>
        </Form.Item>
      </div>
    </div>
  );
};

const AddressBlock = ({ form, prefix, provinces, disabled = false }) => {
  const provinceId = Form.useWatch(`${prefix}ProvinceId`, form);
  const districtId = Form.useWatch(`${prefix}DistrictId`, form);

  const { data: districtData, isFetching: districtFetching } =
    useGetApiListNoToast(
      `account/address/districts/?provinceId=${provinceId || ''}`,
      { enabled: !!provinceId },
    );
  const { data: localLevelData, isFetching: localLevelFetching } =
    useGetApiListNoToast(
      `account/address/local-levels/?districtId=${districtId || ''}`,
      { enabled: !!districtId },
    );

  const districtOptions = (districtData?.data || []).map((d) => ({
    value: d.referenceId,
    label: d.name,
  }));
  const localLevelOptions = (localLevelData?.data || []).map((l) => ({
    value: l.referenceId,
    label: l.levelTypeName ? `${l.name} (${l.levelTypeName})` : l.name,
  }));

  const filterOption = (input, option) =>
    option?.label?.toLowerCase()?.includes(input.toLowerCase());

  return (
    <Row gutter={24}>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item
          className="username"
          label="Province"
          name={`${prefix}ProvinceId`}
        >
          <Select
            showSearch
            allowClear
            disabled={disabled}
            placeholder="Select province"
            options={(provinces || []).map((p) => ({
              value: p.referenceId,
              label: p.name,
            }))}
            filterOption={filterOption}
            onChange={() =>
              form.setFieldsValue({
                [`${prefix}DistrictId`]: undefined,
                [`${prefix}LocalLevelId`]: undefined,
              })
            }
          />
        </Form.Item>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item
          className="username"
          label="District"
          name={`${prefix}DistrictId`}
        >
          <Select
            showSearch
            allowClear
            disabled={disabled || !provinceId}
            loading={districtFetching}
            placeholder="Select district"
            options={districtOptions}
            filterOption={filterOption}
            onChange={() =>
              form.setFieldsValue({ [`${prefix}LocalLevelId`]: undefined })
            }
          />
        </Form.Item>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item
          className="username"
          label="Local Level"
          name={`${prefix}LocalLevelId`}
        >
          <Select
            showSearch
            allowClear
            disabled={disabled || !districtId}
            loading={localLevelFetching}
            placeholder="Select local level"
            options={localLevelOptions}
            filterOption={filterOption}
          />
        </Form.Item>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item
          className="username"
          label="Ward No."
          name={`${prefix}WardNo`}
        >
          <Input disabled={disabled} maxLength={10} onKeyDown={handleKeyDown} />
        </Form.Item>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item className="username" label="Tole" name={`${prefix}Tole`}>
          <Input disabled={disabled} maxLength={200} onBlur={handleInputBlur} />
        </Form.Item>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 8 }}>
        <Form.Item
          className="username"
          label="House No."
          name={`${prefix}HouseNo`}
        >
          <Input disabled={disabled} maxLength={50} />
        </Form.Item>
      </Col>
    </Row>
  );
};

const KycFormModal = ({ open, ledgerId, ledgerName, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const { app } = useAppData();
  const [kycReferenceId, setKycReferenceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [documentFiles, setDocumentFiles] = useState({ fileList: [] });
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const initializedFor = useRef(undefined);
  const sameAsPermanent = Form.useWatch('sameAsPermanentAddress', form);
  const documentType = Form.useWatch('identityDocumentType', form);
  const documentList = documentFiles.fileList || [];

  const { data: provinceData } = useGetApiListNoToast(
    'account/address/provinces/',
    { enabled: open },
  );
  const provinces = provinceData?.data || [];

  const { mutateAsync: createKyc } = usePostApi({ url: 'account/kyc/' });
  const { mutateAsync: updateKyc } = useUpdateApi();

  const toFormValues = (kyc) => {
    const values = { ...kyc };
    delete values.documents;
    delete values.signature;
    (kyc?.familyMembers || []).forEach((member) => {
      const key = familyFieldName(member.relation);
      if (!values[key]) values[key] = member.name;
    });
    if (kyc?.issuedEnglishDate) {
      values.issuedEnglishDatePicker = dayjs(kyc.issuedEnglishDate);
    }
    return values;
  };

  useEffect(() => {
    if (!open) return;
    if (initializedFor.current === ledgerId) return;
    initializedFor.current = ledgerId;
    form.resetFields();
    setKycReferenceId(null);
    setCurrentStep(0);
    setFurthestStep(0);
    setDocumentFiles({ fileList: [] });
    setDeletedDocumentIds([]);

    if (!ledgerId) return;

    (async () => {
      try {
        const resp = await ShowAllData.showDetails(
          `account/ledger/${ledgerId}/kyc/`,
        );
        const latest = resp?.data?.[0];
        if (latest) {
          setKycReferenceId(latest.referenceId);
          setFurthestStep(LAST_STEP);
          form.setFieldsValue(toFormValues(latest));
          setDocumentFiles({ fileList: documentFileList(latest.documents) });
          return;
        }
      } catch (e) {
        /* non-blocking */
      }
      if (ledgerName) form.setFieldsValue({ customerName: ledgerName });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ledgerId]);

  const mirrorPermanentAddress = (changed) => {
    if (
      Object.prototype.hasOwnProperty.call(changed, 'sameAsPermanentAddress')
    ) {
      if (!changed.sameAsPermanentAddress) return;
      const copied = {};
      ADDRESS_PARTS.forEach((part) => {
        copied[`temporary${part}`] = form.getFieldValue(`permanent${part}`);
      });
      form.setFieldsValue(copied);
      return;
    }
    if (!form.getFieldValue('sameAsPermanentAddress')) return;
    const copied = {};
    Object.keys(changed).forEach((key) => {
      if (!key.startsWith('permanent')) return;
      copied[`temporary${key.slice('permanent'.length)}`] = changed[key];
    });
    if (Object.keys(copied).length) form.setFieldsValue(copied);
  };

  const handleDocumentBeforeUpload = async (file) => {
    if (!DOCUMENT_TYPES.includes(file.type)) {
      message.error('Only JPG and PNG images are allowed!');
      return Upload.LIST_IGNORE;
    }
    const compressedFile = await compressImage(file);
    if (!compressedFile) return Upload.LIST_IGNORE;
    if (compressedFile.size / 1024 > MAX_DOCUMENT_SIZE_KB) {
      message.error(`Image must be ${MAX_DOCUMENT_SIZE_KB} KB or smaller.`);
      return Upload.LIST_IGNORE;
    }
    compressedFile.uid = file.uid;
    return tagProcessedFile(file, compressedFile);
  };

  const handleDocumentPreview = (file) => {
    if (file.url || file.thumbUrl) {
      setPreviewImage(file.url || file.thumbUrl);
      return;
    }
    if (file.originFileObj) {
      setPreviewImage(URL.createObjectURL(file.originFileObj));
    }
  };

  const handleDocumentRemove = (file) => {
    if (file.documentId) {
      setDeletedDocumentIds((prev) => [...prev, file.documentId]);
    }
    setDocumentFiles((prev) => ({
      fileList: (prev.fileList || []).filter((item) => item.uid !== file.uid),
    }));
    return true;
  };

  const buildPayload = (values) => {
    const familyMembers = FAMILY_RELATIONS.map(({ value }) => ({
      relation: value,
      name: (values[familyFieldName(value)] || '').trim(),
    })).filter((member) => member.name);

    const address = {};
    ['permanent', 'temporary'].forEach((prefix) => {
      ADDRESS_PARTS.forEach((part) => {
        address[`${prefix}${part}`] = values[`${prefix}${part}`] || '';
      });
    });

    return {
      ledgerId: ledgerId || null,
      englishDate: getTodaysDateMYD(),
      nepaliDate: getTodayDate(),
      salutation: values.salutation || null,
      customerName: (values.customerName || '').trim(),
      maritalStatus: values.maritalStatus || null,
      ...address,
      occupation: values.occupation || null,
      designation: values.designation || null,
      identityDocumentType: values.identityDocumentType || null,
      identityDocumentNumber: values.identityDocumentNumber || null,
      issuedPlace: values.issuedPlace || null,
      issuedEnglishDate: values.issuedEnglishDate || null,
      issuedNepaliDate: values.issuedNepaliDate || null,
      phoneNo: values.phoneNo || null,
      mobileNo: values.mobileNo || null,
      panNo: values.panNo || null,
      email: values.email || null,
      bankName: values.bankName || null,
      bankAccountNo: values.bankAccountNo || null,
      fundSource: values.fundSource || null,
      paymentMode: values.paymentMode || null,
      familyMembers,
    };
  };

  const toMultipart = (payload, newDocuments) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'familyMembers' || value === undefined || value === null)
        return;
      formData.append(key, String(value));
    });
    payload.familyMembers.forEach((member, index) => {
      formData.append(`familyMembers[${index}]relation`, member.relation);
      formData.append(`familyMembers[${index}]name`, member.name);
    });
    newDocuments.forEach((file) => formData.append('document_images', file));
    deletedDocumentIds.forEach((id) =>
      formData.append('delete_document_ids', id),
    );
    return formData;
  };

  const saveKyc = async () => {
    const payload = buildPayload(form.getFieldsValue(true));
    const newDocuments = documentList
      .map((file) => file.originFileObj)
      .filter((file) => file instanceof File);
    const hasFileChanges =
      newDocuments.length > 0 || deletedDocumentIds.length > 0;
    const body = hasFileChanges ? toMultipart(payload, newDocuments) : payload;

    setLoading(true);
    try {
      const response = kycReferenceId
        ? await updateKyc({
            url: `account/kyc/${kycReferenceId}/`,
            values: body,
          })
        : await createKyc(body);
      const saved = response?.data || payload;
      setKycReferenceId(saved?.referenceId || kycReferenceId);
      if (saved?.documents) {
        setDocumentFiles({ fileList: documentFileList(saved.documents) });
        setDeletedDocumentIds([]);
      }
      onSaved?.(saved);
    } finally {
      setLoading(false);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setFurthestStep((prev) => Math.max(prev, step));
  };

  const handleNext = async () => {
    try {
      await form.validateFields(KYC_STEPS[currentStep].fields);
    } catch {
      return;
    }
    goToStep(Math.min(currentStep + 1, LAST_STEP));
  };

  const handleFinish = async () => {
    if (currentStep !== LAST_STEP) {
      goToStep(currentStep + 1);
      return;
    }
    await saveKyc();
  };

  const handleFinishFailed = ({ errorFields }) => {
    const firstField = errorFields?.[0]?.name?.[0];
    const step = KYC_STEPS.findIndex(({ fields }) =>
      fields.includes(firstField),
    );
    if (step >= 0) goToStep(step);
  };

  const documentPairRule = (otherField, messageText) => ({
    validator: (_, value) => {
      const other = form.getFieldValue(otherField);
      if (!value && other) return Promise.reject(messageText);
      return Promise.resolve();
    },
  });

  const stepStyle = (step) => ({
    display: currentStep === step ? 'block' : 'none',
  });

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={false}
      width={980}
      destroyOnHidden={false}
      styles={{ body: bodyStyle }}
      title={
        <div style={{ textAlign: 'center' }}>
          <div>Customer Identification (KYC) Form</div>
          <div style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>
            As per Directive 2077 issued to dealers in precious metals and
            goods, under the Asset Laundering Prevention Act 2064
          </div>
        </div>
      }
    >
      <Steps
        size="small"
        current={currentStep}
        className="tw-my-4"
        onChange={(step) => {
          if (step <= furthestStep) setCurrentStep(step);
        }}
        items={KYC_STEPS.map(({ title }, index) => ({
          title,
          disabled: index > furthestStep,
          status:
            index === currentStep
              ? 'process'
              : index <= furthestStep
                ? 'finish'
                : 'wait',
        }))}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        onValuesChange={mirrorPermanentAddress}
      >
        <div style={stepStyle(0)}>
          <Divider orientation="left" orientationMargin={0}>
            Personal Details
          </Divider>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                className="username"
                label="Salutation"
                name="salutation"
              >
                <Select allowClear placeholder="Select" options={SALUTATIONS} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 10 }}>
              <Form.Item
                className="username"
                label="Customer Full Name"
                name="customerName"
                rules={[
                  {
                    required: true,
                    message: 'Customer name can not be blank.',
                  },
                ]}
                validateTrigger="onChange"
              >
                <Input maxLength={200} onBlur={handleInputBlur} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Marital Status"
                name="maritalStatus"
              >
                <Select
                  allowClear
                  placeholder="Select"
                  options={MARITAL_STATUSES}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left" orientationMargin={0}>
            Contact Details
          </Divider>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item className="username" label="Phone No." name="phoneNo">
                <Input maxLength={50} onKeyDown={handleKeyDown} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                className="username"
                label="Mobile No."
                name="mobileNo"
              >
                <Input maxLength={50} onKeyDown={handleKeyDown} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item className="username" label="PAN No." name="panNo">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                className="username"
                label="Email"
                name="email"
                rules={[{ type: 'email', message: 'Enter a valid email.' }]}
                validateTrigger="onBlur"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={stepStyle(1)}>
          <Divider orientation="left" orientationMargin={0}>
            Permanent Address
          </Divider>
          <AddressBlock form={form} prefix="permanent" provinces={provinces} />

          <Divider orientation="left" orientationMargin={0}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              Temporary Address
              <Form.Item
                name="sameAsPermanentAddress"
                valuePropName="checked"
                noStyle
              >
                <Checkbox style={{ fontWeight: 400 }}>
                  Same as permanent address
                </Checkbox>
              </Form.Item>
            </span>
          </Divider>
          <AddressBlock
            form={form}
            prefix="temporary"
            provinces={provinces}
            disabled={!!sameAsPermanent}
          />
        </div>

        <div style={stepStyle(2)}>
          <Divider orientation="left" orientationMargin={0}>
            Identification Document
          </Divider>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Identification Document"
                name="identityDocumentType"
                dependencies={['identityDocumentNumber']}
                rules={[
                  documentPairRule(
                    'identityDocumentNumber',
                    'Document type is required when document number is provided.',
                  ),
                ]}
              >
                <Select
                  allowClear
                  placeholder="Select document"
                  options={IDENTITY_DOCUMENTS}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Document No."
                name="identityDocumentNumber"
                dependencies={['identityDocumentType']}
                rules={[
                  documentPairRule(
                    'identityDocumentType',
                    'Document number is required when document type is provided.',
                  ),
                ]}
              >
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Issued Place"
                name="issuedPlace"
              >
                <Input maxLength={200} onBlur={handleInputBlur} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <DualDateField
                form={form}
                adName="issuedEnglishDate"
                bsName="issuedNepaliDate"
                label="Issued Date"
                defaultMode={app?.dateType || 'bs'}
              />
            </Col>
          </Row>
          <Form.Item
            className="username"
            label={`Document Images (${documentList.length}/${MAX_DOCUMENT_IMAGES})`}
            extra={
              IDENTITY_DOCUMENT_HINTS[documentType] ||
              `JPG or PNG, up to ${MAX_DOCUMENT_SIZE_KB} KB each. Large photos are compressed automatically.`
            }
          >
            <Upload
              accept={DOCUMENT_ACCEPT}
              listType="picture-card"
              multiple
              maxCount={MAX_DOCUMENT_IMAGES}
              fileList={documentList}
              beforeUpload={handleDocumentBeforeUpload}
              customRequest={customRequest}
              onChange={(info) =>
                imageOnChange(info, documentFiles, (value) =>
                  setDocumentFiles({ fileList: value.fileList }),
                )
              }
              onPreview={handleDocumentPreview}
              onRemove={handleDocumentRemove}
            >
              {documentList.length >= MAX_DOCUMENT_IMAGES ? null : (
                <div>
                  <UploadOutlined /> Upload
                </div>
              )}
            </Upload>
          </Form.Item>
          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: Boolean(previewImage),
                onVisibleChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}
        </div>

        <div style={stepStyle(3)}>
          <Divider orientation="left" orientationMargin={0}>
            Occupation / Employment
          </Divider>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Occupation"
                name="occupation"
              >
                <Input maxLength={200} onBlur={handleInputBlur} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Designation"
                name="designation"
              >
                <Input maxLength={200} onBlur={handleInputBlur} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left" orientationMargin={0}>
            Bank & Fund Source
          </Divider>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item className="username" label="Bank Name" name="bankName">
                <Input onBlur={handleInputBlur} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Account No."
                name="bankAccountNo"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                className="username"
                label="Payment Mode"
                name="paymentMode"
              >
                <Select
                  allowClear
                  placeholder="Select"
                  options={PAYMENT_MODES}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }}>
              <Form.Item
                className="username"
                label="Source of Purchase / Sale Amount"
                name="fundSource"
              >
                <Input.TextArea maxLength={500} autoSize={{ minRows: 1 }} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={stepStyle(4)}>
          <Divider orientation="left" orientationMargin={0}>
            Other Family Members
          </Divider>
          <Row gutter={24}>
            {FAMILY_RELATIONS.map(({ value, label }) => (
              <Col key={value} xs={{ span: 24 }} lg={{ span: 8 }}>
                <Form.Item
                  className="username"
                  label={label}
                  name={familyFieldName(value)}
                >
                  <Input onBlur={handleInputBlur} />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </div>

        <div className="tw-flex tw-justify-between tw-items-center tw-mt-2">
          <AntButton
            disabled={currentStep === 0}
            onClick={() => setCurrentStep((step) => Math.max(step - 1, 0))}
          >
            Back
          </AntButton>
          <Space size="large">
            <AntButton color="red" onClick={onCancel}>
              Cancel
            </AntButton>
            {currentStep < LAST_STEP ? (
              <AntButton key="next" color="green" onClick={handleNext}>
                Next
              </AntButton>
            ) : (
              <AntButton
                key="save"
                color="green"
                loading={loading}
                onClick={() => form.submit()}
              >
                Save
              </AntButton>
            )}
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default KycFormModal;
